import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient, { handleApiError } from "@/services/api/client";
import { ApiResponse } from "@/types/api.types";

// Types
export interface AlertConfig {
  _id?: string;
  name: string;
  minVolumePercent?: number;
  maxPrice?: number;
  minPrice?: number;
  exchanges?: string[];
  enabled: boolean;
  createdAt?: string;
}

export interface AlertsState {
  configs: AlertConfig[];
  triggeredAlerts: any[]; // Using any for triggered list for now
  triggeredCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: AlertsState = {
  configs: [],
  triggeredAlerts: [],
  triggeredCount: 0,
  loading: false,
  error: null,
};

// API
export const alertsApi = {
  getConfigs: async (): Promise<AlertConfig[]> => {
    try {
      const response =
        await apiClient.get<ApiResponse<{ alerts: AlertConfig[] }>>(
          "/alerts/config",
        );
      return response.data.data.alerts || []; // Adjusting based on potential response wrap
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  createConfig: async (
    config: Omit<AlertConfig, "_id">,
  ): Promise<AlertConfig> => {
    try {
      const response = await apiClient.post<ApiResponse<AlertConfig>>(
        "/alerts/config",
        config,
      );
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  updateConfig: async ({
    id,
    config,
  }: {
    id: string;
    config: Partial<AlertConfig>;
  }): Promise<AlertConfig> => {
    try {
      const response = await apiClient.put<ApiResponse<AlertConfig>>(
        `/alerts/config/${id}`,
        config,
      );
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  deleteConfig: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/alerts/config/${id}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

// Async Thunks
export const fetchAlertConfigs = createAsyncThunk(
  "alerts/fetchConfigs",
  async (_, { rejectWithValue }) => {
    try {
      const configs = await alertsApi.getConfigs();
      return configs;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch alert configurations");
    }
  },
);

export const createAlertConfig = createAsyncThunk(
  "alerts/createConfig",
  async (config: Omit<AlertConfig, "_id">, { rejectWithValue }) => {
    try {
      const newConfig = await alertsApi.createConfig(config);
      return newConfig;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to create alert configuration");
    }
  },
);

export const updateAlertConfig = createAsyncThunk(
  "alerts/updateConfig",
  async (
    { id, config }: { id: string; config: Partial<AlertConfig> },
    { rejectWithValue },
  ) => {
    try {
      const updatedConfig = await alertsApi.updateConfig({ id, config });
      return updatedConfig;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to update alert configuration");
    }
  },
);

export const deleteAlertConfig = createAsyncThunk(
  "alerts/deleteConfig",
  async (id: string, { rejectWithValue }) => {
    try {
      await alertsApi.deleteConfig(id);
      return id;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to delete alert configuration");
    }
  },
);

// Slice
const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Configs
    builder
      .addCase(fetchAlertConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlertConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.configs = action.payload;
        state.error = null;
      })
      .addCase(fetchAlertConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Config
    builder
      .addCase(createAlertConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAlertConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.configs.push(action.payload);
        state.error = null;
      })
      .addCase(createAlertConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Config
    builder
      .addCase(updateAlertConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAlertConfig.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.configs.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (index !== -1) {
          state.configs[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAlertConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Config
    builder
      .addCase(deleteAlertConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAlertConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.configs = state.configs.filter((c) => c._id !== action.payload);
        state.error = null;
      })
      .addCase(deleteAlertConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = alertsSlice.actions;

export const selectAlertConfigs = (state: { alerts: AlertsState }) =>
  state.alerts.configs;
export const selectAlertsLoading = (state: { alerts: AlertsState }) =>
  state.alerts.loading;
export const selectAlertsError = (state: { alerts: AlertsState }) =>
  state.alerts.error;

export default alertsSlice.reducer;
